import React, { useState, useEffect } from 'react';
import { addDoc, updateDoc, doc, collection, serverTimestamp, getDocs, query, orderBy, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { Client, User, CheckIn, sellerDisplayName } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useAccessScope } from '../hooks/useAccessScope';
import { fetchCheckinsEscopado } from '../services/checkinsQuery';

interface Props {
  client: Client | null; // null = novo cliente
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = {
  name: '', tradeName: '', cnpj: '', stateRegistration: '', phone: '', email: '',
  street: '', number: '', complement: '', neighborhood: '', city: '', state: '', zipCode: '',
  latitude: '', longitude: '', notes: '', potencialCompra: '', potencialVenda: '',
};

// Converte texto de valor em número, aceitando pt-BR e US sem ambiguidade. Vazio/inválido → null.
// "1.234,56"→1234.56 | "1,234.56"→1234.56 | "1500.50"→1500.5 | "1.500"→1500 | "1500,5"→1500.5
const parseValorBRL = (s: string): number | null => {
  let t = String(s || '').replace(/[^\d.,-]/g, '').trim();
  if (!t) return null;
  const hasComma = t.includes(','), hasDot = t.includes('.');
  if (hasComma && hasDot) {
    // o ÚLTIMO separador é o decimal; o outro é milhar
    if (t.lastIndexOf(',') > t.lastIndexOf('.')) t = t.replace(/\./g, '').replace(',', '.');
    else t = t.replace(/,/g, '');
  } else if (hasComma) {
    t = t.replace(',', '.'); // vírgula = decimal (pt-BR)
  } else if (hasDot) {
    // ponto sozinho é ambíguo: "1.500"/"15.000" (milhar) vs "1500.50"/"1.5" (decimal).
    const afterLast = t.slice(t.lastIndexOf('.') + 1);
    const dots = t.split('.').length - 1;
    if (dots > 1 || afterLast.length === 3) t = t.replace(/\./g, ''); // milhar
    // senão mantém o ponto como decimal
  }
  const n = Number(t);
  return isNaN(n) ? null : n;
};
const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const RESULTADO_LABEL: Record<string, string> = {
  comprador_atendeu: 'Comprador atendeu',
  comprador_ausente: 'Comprador ausente',
  comprador_nao_atendeu: 'Não atendeu',
};

export default function ClientFormModal({ client, onClose, onSaved }: Props) {
  const { user, isAdmin } = useAuth();
  const scope = useAccessScope(); // escopo de leitura (equipe do gestor) usado no histórico do cliente
  // EDIÇÃO do cadastro do cliente é EXCLUSIVA do papel literal `admin` — os demais perfis (diretor,
  // pricing, gerente nacional, gerente, coordenador, supervisor, vendedor) abrem a ficha em SOMENTE
  // LEITURA. Só vale ao EDITAR: criar cliente novo continua liberado a quem já podia (vendedor/admin),
  // por isso o `!!client`. A trava de verdade está nas firestore.rules; isto é a camada de UI.
  const somenteLeitura = !!client && user?.role !== 'admin';
  const [form, setForm] = useState(emptyForm);
  const [simplesNacional, setSimplesNacional] = useState(false);
  const [estrelas, setEstrelas] = useState<number | null>(null); // null = sem classificação (não avaliado)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cnpjStatus, setCnpjStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [cnpjMsg, setCnpjMsg] = useState('');
  const [dupMsg, setDupMsg] = useState('');
  const [sellers, setSellers] = useState<User[]>([]);
  const [sellerId, setSellerId] = useState('');       // uid do vendedor-usuário do app (se o CODVEND casar com um)
  const [sellerCod, setSellerCod] = useState('');     // CODVEND selecionado — permite QUALQUER vendedor (o que é salvo)
  const [sellerNome, setSellerNome] = useState('');   // nome do vendedor selecionado
  const [sellerInput, setSellerInput] = useState(''); // texto digitado (código OU nome)
  const [sellerOpcoes, setSellerOpcoes] = useState<{ codvend: number; nome: string }[]>([]);
  const [sellerBusca, setSellerBusca] = useState<'idle' | 'loading' | 'ok' | 'nao'>('idle');
  const [historico, setHistorico] = useState<CheckIn[]>([]);
  const [loadingHist, setLoadingHist] = useState(false);

  // Carrega vendedores ativos para o seletor (somente admin precisa/pode listar usuários)
  useEffect(() => {
    if (!isAdmin) return;
    const loadSellers = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'users'), orderBy('name')));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() })) as User[];
        setSellers(all.filter(u => u.role === 'seller' && u.active !== false));
      } catch { /* ignore */ }
    };
    loadSellers();
  }, [isAdmin]);

  // Seleciona um vendedor da lista: fixa o CODVEND salvo, vincula o `sellerId` se ele for usuário do
  // app (casa por codVendSankhya) e escreve o rótulo "cod - nome" no campo.
  const commitVendedor = (codvend: number, nome: string) => {
    const cod = String(codvend);
    const u = sellers.find(s => String(s.codVendSankhya ?? '') === cod);
    setSellerCod(cod); setSellerNome(nome); setSellerId(u ? u.id : '');
    setSellerInput(`${cod} - ${nome}`); setSellerOpcoes([]); setSellerBusca('ok');
  };

  // Typeahead: busca vendedores por CÓDIGO ou NOME (debounce) na TGFVEN — qualquer vendedor, não só
  // usuários do app. Enquanto o texto diverge da seleção, o commit é zerado (só um clique na lista
  // re-seleciona) → nunca salva um vendedor diferente do que está escrito. Código exato e único
  // auto-seleciona (mantém o fluxo rápido de digitar só o código).
  useEffect(() => {
    if (!isAdmin) return;
    const termo = sellerInput.trim();
    if (sellerCod && termo === `${sellerCod} - ${sellerNome}`) { setSellerOpcoes([]); setSellerBusca('ok'); return; }
    setSellerCod(''); setSellerNome(''); setSellerId('');
    if (termo.length < 2) { setSellerOpcoes([]); setSellerBusca('idle'); return; }
    setSellerBusca('loading'); setSellerOpcoes([]);
    const t = setTimeout(async () => {
      try {
        const fn = httpsCallable(functions, 'buscarVendedores');
        const r: any = await fn({ termo });
        const lista: { codvend: number; nome: string }[] = r?.data?.vendedores || [];
        const exato = /^\d+$/.test(termo) ? lista.filter(v => v.codvend === Number(termo)) : [];
        if (exato.length === 1) { commitVendedor(exato[0].codvend, exato[0].nome); return; }
        setSellerOpcoes(lista); setSellerBusca(lista.length ? 'idle' : 'nao');
      } catch { setSellerOpcoes([]); setSellerBusca('nao'); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerInput, isAdmin, sellers]);

  useEffect(() => {
    if (client) {
      setSellerId((client as any).sellerId || '');
      // Pré-preenche o vendedor: código do sankhya.codvend (ou do prefixo "CODVEND - " do sellerName),
      // e o nome/rótulo já vem do sellerName gravado.
      const codDoSankhya = String((client as any).sankhya?.codvend ?? '').trim();
      const rawName = client.sellerName || '';
      const codDoNome = String(rawName.match(/^\s*(\d+)\s*-/)?.[1] || '');
      const cod = codDoSankhya || codDoNome;
      const nome = rawName.replace(/^\s*\d+\s*-\s*/, '').trim();
      setSellerCod(cod);
      setSellerNome(nome);
      setSellerInput(rawName || cod);
      setSellerBusca(cod ? 'ok' : 'idle');
      setForm({
        name: client.name || '',
        tradeName: (client as any).tradeName || '',
        cnpj: client.cnpj || '',
        stateRegistration: client.stateRegistration || '',
        phone: client.phone || '',
        email: client.email || '',
        street: client.address?.street || '',
        number: client.address?.number || '',
        complement: client.address?.complement || '',
        neighborhood: client.address?.neighborhood || '',
        city: client.address?.city || '',
        state: client.address?.state || '',
        zipCode: client.address?.zipCode || '',
        latitude: client.location?.latitude?.toString() || '',
        longitude: client.location?.longitude?.toString() || '',
        notes: client.notes || '',
        potencialCompra: client.potencialCompra || '',
        potencialVenda: client.potencialVenda != null ? String(client.potencialVenda).replace('.', ',') : '',
      });
      setSimplesNacional(client.simplesNacional ?? false);
      setEstrelas(typeof (client as any).estrelas === 'number' ? (client as any).estrelas : null);
    }
  }, [client]);

  // Histórico de visitas/observações (todos os check-outs deste cliente) — só ao editar.
  useEffect(() => {
    if (!client) { setHistorico([]); return; }
    let alive = true;
    setLoadingHist(true);
    (async () => {
      try {
        // ESCOPO: helper único — acesso total lê todas as visitas do cliente; GESTOR só as da equipe
        // (lotes de sellerId, porque a regra recusa consulta ampla dele); vendedor só as próprias.
        // Regra do Firestore não filtra: sem o where por sellerId ela RECUSA a consulta inteira.
        // Só igualdades (não exige índice composto); ordena no cliente.
        const rows0 = await fetchCheckinsEscopado(user, scope, { clientId: client.id });
        const rows = rows0.map((r: any) => ({
          ...r, createdAt: r.createdAt?.toDate?.() ?? null, checkoutAt: r.checkoutAt?.toDate?.() ?? null,
        })) as CheckIn[];
        rows.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
        if (alive) setHistorico(rows);
      } catch { if (alive) setHistorico([]); }
      finally { if (alive) setLoadingHist(false); }
    })();
    return () => { alive = false; };
  }, [client]);

  const set = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }));

  // Busca dados da empresa pela CNPJá Open API ao digitar o CNPJ completo
  const lookupCnpj = async (digits: string) => {
    setCnpjStatus('loading');
    setCnpjMsg('Buscando dados do CNPJ...');
    try {
      const res = await fetch(`https://open.cnpja.com/office/${digits}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('CNPJ não encontrado na Receita.');
        if (res.status === 429) throw new Error('Muitas consultas seguidas. Aguarde 1 minuto e tente de novo.');
        throw new Error('Falha na consulta. Preencha manualmente.');
      }
      const d = await res.json();
      const addr = d.address || {};
      const phone = d.phones?.[0] ? `(${d.phones[0].area}) ${d.phones[0].number}` : '';
      const email = d.emails?.[0]?.address || '';
      const ie = d.registrations?.find((r: any) => r.enabled)?.number || d.registrations?.[0]?.number || '';

      setForm(prev => ({
        ...prev,
        name: d.company?.name || prev.name,
        tradeName: d.alias || prev.tradeName,
        stateRegistration: ie || prev.stateRegistration,
        email: email || prev.email,
        phone: phone || prev.phone,
        street: addr.street || prev.street,
        number: addr.number || prev.number,
        complement: addr.details || prev.complement,
        neighborhood: addr.district || prev.neighborhood,
        city: addr.city || prev.city,
        state: addr.state || prev.state,
        zipCode: addr.zip ? String(addr.zip).replace(/\D/g, '') : prev.zipCode,
      }));
      setSimplesNacional(!!d.company?.simples?.optant);

      const ativa = d.status?.text ? ` • Situação: ${d.status.text}` : '';
      const simples = d.company?.simples?.optant ? ' • Optante do Simples' : '';
      setCnpjStatus('ok');
      setCnpjMsg(`Dados preenchidos automaticamente.${ativa}${simples}. Confira antes de salvar.`);
    } catch (e: any) {
      setCnpjStatus('error');
      setCnpjMsg(e.message || 'Não foi possível consultar o CNPJ. Preencha manualmente.');
    }
  };

  const handleCnpjChange = (value: string) => {
    set('cnpj', value);
    const digits = value.replace(/\D/g, '');
    if (digits.length === 14) {
      lookupCnpj(digits);
    } else {
      setCnpjStatus('idle');
      setCnpjMsg('');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.cnpj.trim() || !form.zipCode.trim()) {
      setError('Preencha os campos obrigatórios: Razão Social, CNPJ e CEP.');
      return;
    }
    // Vendedor responsável: acesso total SELECIONA por código ou nome (qualquer vendedor, não só usuário
    // do app). Ao EDITAR sem ser acesso total (gestor/vendedor), PRESERVA o vendedor atual do cliente —
    // senão salvar reatribuiria o cliente pra quem editou. Ao CRIAR (não-admin), é o próprio usuário.
    const codNum = Number(sellerCod.trim());
    if (isAdmin && !(codNum > 0)) {
      setError('Selecione o vendedor responsável (por código ou nome).');
      return;
    }
    const finalSellerId = isAdmin ? sellerId : (client ? ((client as any).sellerId || '') : (user?.id || ''));
    if (!isAdmin && !finalSellerId && !client) {
      setError('Selecione o vendedor responsável.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const data: any = {
        name: form.name.trim(),
        tradeName: form.tradeName.trim() || null,
        cnpj: form.cnpj.trim() || null,
        stateRegistration: form.stateRegistration.trim() || null,
        simplesNacional: simplesNacional,
        estrelas: estrelas,
        potencialCompra: form.potencialCompra.trim() || null,
        potencialVenda: parseValorBRL(form.potencialVenda),
        sellerId: finalSellerId || null,
        sellerName: isAdmin
          ? `${codNum} - ${sellerNome || 'vendedor'}`
          : (client ? (client.sellerName ?? null) : (user ? sellerDisplayName(user) : null)),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: {
          street: form.street.trim(),
          number: form.number.trim(),
          complement: form.complement.trim() || null,
          neighborhood: form.neighborhood.trim(),
          city: form.city.trim(),
          state: form.state.trim().toUpperCase(),
          zipCode: form.zipCode.trim(),
        },
        notes: form.notes.trim() || null,
      };

      const lat = parseFloat(form.latitude);
      const lng = parseFloat(form.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        data.location = { latitude: lat, longitude: lng };
      } else {
        data.location = null;
      }

      if (client) {
        // A coordenada confirmada em campo (geoPrecision 'gps') é verdade e não pode ser apagada nem
        // re-geocodificada por uma edição de cadastro. Só re-valida (geoCity=null) quando o gestor
        // realmente alterou a coordenada; se não alterou — ou limpou os campos sem querer — preserva a de campo.
        const oLat = client.location?.latitude ?? null, oLng = client.location?.longitude ?? null;
        const nLat = data.location?.latitude ?? null, nLng = data.location?.longitude ?? null;
        const coordMudou = oLat !== nLat || oLng !== nLng;
        if (client.geoPrecision === 'gps' && (!coordMudou || nLat === null)) {
          delete data.location; // mantém a coordenada confirmada em campo (não sobrescreve com vazio/igual)
        } else {
          data.geoCity = null; // endereço/coordenada podem ter mudado → rotina revalida a cidade da coordenada
          // ⚠️ O CONTADOR VAI JUNTO, e este é o ponto mais importante dos três: é aqui que o
          // comercial conserta o endereço depois de o sistema desistir. Se o cliente voltasse com o
          // teto já gasto, a primeira rodada ruim o selaria de novo e o trabalho do comercial seria
          // jogado fora — o mesmo defeito de uma-tentativa-sela, com outro nome.
          data.geoDivergTentativas = 0;
        }
        // Admin pode trocar o CODVEND: grava só `sankhya.codvend` (dot-path preserva codparc/status).
        if (isAdmin) data['sankhya.codvend'] = codNum;
        await updateDoc(doc(db, 'clients', client.id), data);
      } else {
        // Verifica no Sankhya ANTES de criar (evita duplicado/falsa impressão de cadastro)
        try {
          const verificar = httpsCallable(functions, 'verificarParceiroSankhya');
          const resp: any = await verificar({ cnpj: form.cnpj, ie: form.stateRegistration });
          if (resp.data?.existe) {
            setDupMsg(
              `Este cliente já está cadastrado no Sankhya por ${resp.data.motivo} ` +
              `(parceiro ${resp.data.codparc}${resp.data.nome ? ' - ' + resp.data.nome : ''}). ` +
              `O cadastro não foi realizado para evitar duplicidade.`
            );
            setSaving(false);
            return;
          }
        } catch (e) {
          // Se a verificação falhar (ex: Sankhya fora do ar), segue o cadastro normalmente
          console.warn('Verificação no Sankhya falhou, prosseguindo:', e);
        }

        await addDoc(collection(db, 'clients'), {
          ...data,
          // CODVEND digitado (admin) → o `syncClienteSankhya` cria o Parceiro com esse vendedor, e o
          // escopo dos gestores casa por aqui. Sync sobrescreve o objeto `sankhya` depois (com o mesmo codvend).
          ...(isAdmin ? { sankhya: { codvend: codNum } } : {}),
          active: true,
          createdBy: user?.id || null,
          createdByName: user?.name || null,
          createdAt: serverTimestamp(),
        });
      }
      onSaved();
    } catch (e) {
      setError('Não foi possível salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{client ? 'Editar Cliente' : 'Novo Cliente'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>
          {error && <div style={styles.error}>{error}</div>}

          {/* fieldset disabled desabilita NATIVAMENTE todos os inputs/selects/textareas aninhados —
              evita ter que passar `disabled` campo a campo. Some quando é criação ou quando é admin. */}
          <fieldset disabled={somenteLeitura} style={styles.fieldset}>
          {isAdmin && (
            <>
              <p style={styles.section}>Vendedor</p>
              <label style={styles.label}>Vendedor responsável *</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={styles.select}
                  value={sellerInput}
                  onChange={e => setSellerInput(e.target.value)}
                  placeholder="Digite o código (ex: 822) ou o nome do vendedor"
                  autoComplete="off"
                />
                {sellerOpcoes.length > 0 && (
                  <div style={styles.dropdown}>
                    {sellerOpcoes.map(v => (
                      <div
                        key={v.codvend}
                        style={styles.dropdownItem}
                        onMouseDown={e => { e.preventDefault(); commitVendedor(v.codvend, v.nome); }}
                      >
                        <strong>{v.codvend}</strong> — {v.nome}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p style={{
                margin: '4px 2px 0', fontSize: 13,
                color: sellerBusca === 'nao' ? '#dc2626' : sellerBusca === 'ok' ? '#16a34a' : '#6b7280',
              }}>
                {sellerBusca === 'ok' && sellerCod
                  ? `✅ ${sellerCod} - ${sellerNome}`
                  : sellerBusca === 'loading' ? '⏳ Buscando vendedor...'
                  : sellerBusca === 'nao' ? '⚠️ Nenhum vendedor encontrado.'
                  : sellerOpcoes.length > 0 ? 'Selecione o vendedor na lista.'
                  : 'Qualquer vendedor do Sankhya — busque por código ou nome.'}
              </p>
            </>
          )}

          <p style={styles.section}>Dados do Cliente</p>
          <Input label="Razão Social *" value={form.name} onChange={v => set('name', v)} placeholder="Razão social da empresa" />
          <Input label="Nome Fantasia" value={form.tradeName} onChange={v => set('tradeName', v)} placeholder="Nome fantasia" />
          <div style={styles.row}>
            <Input label="CNPJ *" value={form.cnpj} onChange={handleCnpjChange} placeholder="Digite o CNPJ para buscar automaticamente" />
            <Input label="Telefone" value={form.phone} onChange={v => set('phone', v)} placeholder="(00) 00000-0000" />
          </div>
          {cnpjMsg && (
            <p style={{
              ...styles.cnpjMsg,
              color: cnpjStatus === 'error' ? '#dc2626' : cnpjStatus === 'ok' ? '#16a34a' : '#f5821f',
            }}>
              {cnpjStatus === 'loading' ? '⏳ ' : cnpjStatus === 'ok' ? '✅ ' : cnpjStatus === 'error' ? '⚠️ ' : ''}
              {cnpjMsg}
            </p>
          )}
          <div style={styles.row}>
            <Input label="Inscrição Estadual" value={form.stateRegistration} onChange={v => set('stateRegistration', v)} placeholder="ISENTO ou número" />
            <Input label="E-mail" value={form.email} onChange={v => set('email', v)} placeholder="email@empresa.com" />
          </div>
          <label style={styles.checkboxRow}>
            <input type="checkbox" checked={simplesNacional} onChange={e => setSimplesNacional(e.target.checked)} />
            <span>Optante do Simples Nacional</span>
          </label>

          <div style={{ marginTop: 8 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 }}>
              Potencial (estrelas) — define a cadência de visita
            </label>
            <select style={styles.select} value={estrelas == null ? '' : estrelas} onChange={e => setEstrelas(e.target.value === '' ? null : Number(e.target.value))}>
              <option value="">Sem classificação (não avaliado)</option>
              <option value={0}>0 ★ — Esporádico (sem ciclo)</option>
              <option value={1}>1 ★ — a cada 90 dias</option>
              <option value={2}>2 ★ — a cada 60 dias</option>
              <option value={3}>3 ★ — mensal (30 dias)</option>
            </select>
          </div>

          <Input label="Potencial de Venda (R$)" value={form.potencialVenda} onChange={v => set('potencialVenda', v)} placeholder="Venda projetada, ex.: 15.000,00" />
          <div style={{ marginBottom: 12 }}>
            <label style={styles.label}>Potencial de Compra</label>
            <textarea
              style={styles.textarea}
              placeholder="Observações do potencial de compra (produtos, volume, oportunidades...)"
              value={form.potencialCompra}
              onChange={e => set('potencialCompra', e.target.value)}
            />
          </div>

          <p style={styles.section}>Endereço</p>
          <Input label="Rua" value={form.street} onChange={v => set('street', v)} placeholder="Nome da rua/avenida" />
          <div style={styles.row}>
            <Input label="Número" value={form.number} onChange={v => set('number', v)} placeholder="123" flex={1} />
            <Input label="Complemento" value={form.complement} onChange={v => set('complement', v)} placeholder="Sala, Andar..." flex={2} />
          </div>
          <Input label="Bairro" value={form.neighborhood} onChange={v => set('neighborhood', v)} placeholder="Bairro" />
          <div style={styles.row}>
            <Input label="Cidade" value={form.city} onChange={v => set('city', v)} placeholder="Cidade" flex={3} />
            <Input label="UF" value={form.state} onChange={v => set('state', v)} placeholder="SP" flex={1} maxLength={2} />
          </div>
          <Input label="CEP *" value={form.zipCode} onChange={v => set('zipCode', v)} placeholder="00000-000" />

          <p style={styles.section}>Localização GPS (opcional)</p>
          {client && (() => {
            const gp = client.geoPrecision;
            const badge =
              gp === 'gps' ? { txt: '📍 Confirmado em campo (check-in)', bg: '#dcfce7', fg: '#15803d' }
              : gp === 'street' || gp === 'postal' ? { txt: '✓ Endereço preciso', bg: '#dbeafe', fg: '#1d4ed8' }
              : gp === 'erp' ? { txt: 'Coordenada do Sankhya (ERP)', bg: '#e0e7ff', fg: '#4338ca' }
              : gp === 'city' ? { txt: '⚠️ Centro da cidade (impreciso)', bg: '#fef3c7', fg: '#b45309' }
              : (client.location?.latitude ? { txt: 'Coordenada não classificada', bg: '#f3f4f6', fg: '#6b7280' } : null);
            return badge ? <span style={{ ...styles.geoBadge, background: badge.bg, color: badge.fg }}>{badge.txt}</span> : null;
          })()}
          <p style={styles.hint}>Preencha para o vendedor navegar direto pelo mapa. Pegue no Google Maps: clique no local → coordenadas. <strong>Quando o vendedor faz check-in no cliente, a localização pode ser confirmada em campo (📍).</strong></p>
          <div style={styles.row}>
            <Input label="Latitude" value={form.latitude} onChange={v => set('latitude', v)} placeholder="-23.55052" />
            <Input label="Longitude" value={form.longitude} onChange={v => set('longitude', v)} placeholder="-46.633308" />
          </div>

          <p style={styles.section}>Observações</p>
          <textarea
            style={styles.textarea}
            placeholder="Informações adicionais..."
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
          />

          {client && (
            <>
              <p style={styles.section}>Histórico de visitas</p>
              {loadingHist ? (
                <p style={styles.hint}>Carregando visitas…</p>
              ) : historico.length === 0 ? (
                <p style={styles.hint}>Nenhuma visita registrada para este cliente.</p>
              ) : (
                <div style={styles.histList}>
                  {historico.map(v => {
                    const data = v.createdAt
                      ? new Date(v.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '—';
                    const res = v.resultado ? RESULTADO_LABEL[v.resultado] : null;
                    const pedido =
                      v.pedidoStatus === 'sim' ? `Pedido${v.pedidoValor != null ? ' ' + fmtBRL(v.pedidoValor) : ''}`
                      : v.pedidoStatus === 'orcamento' ? `Orçamento${v.pedidoValor != null ? ' ' + fmtBRL(v.pedidoValor) : ''}`
                      : v.pedidoStatus === 'nao' ? 'Sem pedido' : null;
                    return (
                      <div key={v.id} style={styles.histItem}>
                        <div style={styles.histHead}>
                          <span style={styles.histDate}>{data}</span>
                          {v.sellerName && <span style={styles.histSeller}>{v.sellerName}</span>}
                        </div>
                        <div style={styles.histTags}>
                          {res && <span style={styles.histTag}>{res}</span>}
                          {pedido && <span style={{ ...styles.histTag, background: '#dcfce7', color: '#15803d' }}>{pedido}</span>}
                          {!v.checkoutAt && <span style={{ ...styles.histTag, background: '#fef3c7', color: '#b45309' }}>Em aberto</span>}
                        </div>
                        {v.notes && <p style={styles.histNotes}>{v.notes}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          </fieldset>
        </div>

        <div style={styles.footer}>
          {somenteLeitura && (
            <span style={{ flex: 1, fontSize: 13, color: '#6b7280' }}>
              👁️ Somente leitura — apenas o administrador edita o cadastro do cliente.
            </span>
          )}
          <button style={styles.cancelBtn} onClick={onClose}>{somenteLeitura ? 'Fechar' : 'Cancelar'}</button>
          {!somenteLeitura && (
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          )}
        </div>
      </div>

      {dupMsg && (
        <div style={styles.dupOverlay} onClick={() => setDupMsg('')}>
          <div style={styles.dupModal} onClick={e => e.stopPropagation()}>
            <button style={styles.dupClose} onClick={() => setDupMsg('')}>✕</button>
            <div style={styles.dupIcon}>⚠️</div>
            <h3 style={styles.dupTitle}>Cliente já cadastrado</h3>
            <p style={styles.dupText}>{dupMsg}</p>
            <button style={styles.dupBtn} onClick={() => setDupMsg('')}>Entendi</button>
          </div>
        </div>
      )}
    </div>
  );
}

interface InputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  flex?: number;
  maxLength?: number;
}

function Input({ label, value, onChange, placeholder, flex, maxLength }: InputProps) {
  return (
    <div style={{ flex: flex || undefined, marginBottom: 12, width: flex ? undefined : '100%' }}>
      <label style={styles.label}>{label}</label>
      <input
        style={styles.input}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, width: '90%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f0f0f0' },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' },
  body: { padding: '16px 24px', overflowY: 'auto', flex: 1 },
  section: { fontSize: 12, fontWeight: 700, color: '#f5821f', textTransform: 'uppercase', margin: '16px 0 10px' },
  hint: { fontSize: 12, color: '#888', margin: '-6px 0 10px' },
  geoBadge: { display: 'inline-block', fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '4px 12px', margin: '0 0 8px' },
  cnpjMsg: { fontSize: 12, margin: '-4px 0 10px', fontWeight: 500 },
  dupOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  dupModal: { position: 'relative', background: '#fff', borderRadius: 16, padding: '32px 28px', width: '90%', maxWidth: 420, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' },
  dupClose: { position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' },
  dupIcon: { fontSize: 44, marginBottom: 8 },
  dupTitle: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: '0 0 10px' },
  dupText: { fontSize: 15, color: '#444', lineHeight: 1.5, margin: '0 0 24px' },
  dupBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 32px', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#444', cursor: 'pointer', marginBottom: 4 },
  row: { display: 'flex', gap: 10 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 },
  input: { width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, boxSizing: 'border-box' },
  select: { width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, boxSizing: 'border-box', background: '#fff' },
  fieldset: { border: 'none', margin: 0, padding: 0, minWidth: 0 }, // só container do `disabled`; sem visual próprio
  dropdown: { position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto' },
  dropdownItem: { padding: '9px 12px', fontSize: 14, color: '#333', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' },
  textarea: { width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, boxSizing: 'border-box', minHeight: 70, fontFamily: 'inherit', resize: 'vertical' },
  error: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 14 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px', borderTop: '1px solid #f0f0f0' },
  cancelBtn: { background: '#f3f4f6', color: '#444', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  saveBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  histList: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 4 },
  histItem: { border: '1px solid #eee', borderRadius: 10, padding: '10px 12px', background: '#fafafa' },
  histHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  histDate: { fontSize: 13, fontWeight: 700, color: '#1a1a2e' },
  histSeller: { fontSize: 12, color: '#888' },
  histTags: { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 0' },
  histTag: { fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '2px 9px', background: '#eef2ff', color: '#4338ca' },
  histNotes: { fontSize: 13, color: '#444', lineHeight: 1.4, margin: '8px 0 0', whiteSpace: 'pre-wrap' },
};
